import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ErrorBoundary from 'components/ErrorBoundary';
import Modal from 'components/CncUI/Modal';
import Button from 'components/CncUI/Button';
import Typography from 'components/CncUI/Typography';
import AccountsTable from './Plaid/InfoPanel/AccountsTable';

import useCountry from 'hooks/useCountry';
import formatDeposit from './utils/formatDeposit';

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  right: {
    textAlign: 'right',
  },
  button: {
    marginLeft: spacing(1),
  },
  existing: {
    '& .MuiPaper-root': {
      [breakpoints.up('lg')]: {
        maxWidth: 1030,
      },
    },
    '& .cncui-modal-content': {
      padding: spacing(2),
      overflowX: 'hidden',
    },
  },
}));

const CopyAccountsModal = ({
  closeModal,
  cancelSetup,
  startSetup,
  directDeposits,
  mappings,
  dbCode,
  onSave,
}) => {
  const classes = useStyles();
  const { country } = useCountry();
  const [loading, setLoading] = useState(false);
  const isCanada = country === 'CAN'; 

  const displayDD = directDeposits.filter(
    dd => (dd.forCorps || '').toLowerCase() !== 'c',
  );
  const { isDeliveryRequested: delivery = false, isPaperLess: paperless = true } = displayDD[0];
  const amountType = displayDD[0].flatAmount ? '$' : '%';

  const errorHandlerBadAccounts = () => {
    const accountsMetadata = directDeposits.map(account => ({
      id: account.id,
      isDirectDeposit: account.isDirectDeposit,
      splitPercentage: account.splitPercentage,
      bankAccountType: account.bankAccountType,
      isPaperLess: isCanada ? false : account.isPaperLess,
      isDeliveryRequested: account.isDeliveryRequested,
    }));
    Sentry.setContext('accountMetadata', accountsMetadata);
  };

  const copyAccounts = () => {
    setLoading(true);
    const primary = displayDD.find(a => a.primary);
    const splitAccounts = displayDD.filter(a => !a.primary);
    const splitType = amountType === '%' ? 1 : 2; // defined by FMS
    const formatSetup = {
      dbCode,
      amountType,
      directDeposits: displayDD,
      isCanada,
      paperless,
      delivery,
    };
    const formData = [];

    // Delete previous  records
    const depositsToDelete = directDeposits.filter(a => a.id);
    if (depositsToDelete.length > 0) {
      depositsToDelete.forEach(deposit => {
        formData.push({
          ...formatDeposit(deposit, formatSetup),
          isDirectDeposit: false,
        });
      });
    }

    const pushSet = (forCorps = 'i') => {
      if (splitAccounts.length > 0) {
        splitAccounts.forEach((d, i) => {
          const deposit = {
            ...formatDeposit(d, formatSetup),
            id: null,
            splitType,
            sequence: i + 1,
            forCorps,
          };
          formData.push(deposit);
        });
      }

      if (primary) {
        const primaryDeposit = {
          ...formatDeposit(primary, formatSetup),
          id: null,
          splitType,
          sequence: displayDD.length,
          forCorps,
        };
        formData.push(primaryDeposit);
      }
    };

    // Copy in the individual setup
    pushSet('i');
    // Copy in the corp setup
    pushSet('c');

    try {
      onSave({
        plaid: true,
        basicInfo: { dbCode },
        directDeposits: formData || null,
      }).then(() => {
        closeModal();
      });
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  return (
    <Modal open={true} className={classes.existing}>
      <Modal.Content>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography h4 noMargin>
                  Use existing direct deposit setup?
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography short body2 noMargin>
                  Do you want to apply the direct deposit setup used in your W-2
                  employee profile? You’ll still be able to make modifications
                  to this setup.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <ErrorBoundary alert errorHandler={errorHandlerBadAccounts}>
              <AccountsTable
                mappings={mappings}
                directDeposits={displayDD}
                setPaperless={() => {}}
              />
            </ErrorBoundary>
          </Grid>
          <Grid item xs={12} className={classes.right}>
            <Button
              tertiary
              onClick={cancelSetup}
              disabled={loading}
              className={classes.button}
            >
              I’ll set up direct deposit later
            </Button>
            <Button
              secondary
              onClick={startSetup}
              disabled={loading}
              className={classes.button}
            >
              No, create new
            </Button>
            <Button
              onClick={copyAccounts}
              loading={loading}
              className={classes.button}
            >
              Yes, use existing
            </Button>
          </Grid>
        </Grid>
      </Modal.Content>
    </Modal>
  );
};

export default CopyAccountsModal;
